import type { AlertsManagerWidgetState, AlertsManagerWidgetStateParams } from '@repo/go-widgets';
import { action, makeObservable, observable } from 'mobx';

import { BaseWidgetEntityWithWellId } from 'src/entities/widget/BaseWidgetEntityWithWellId';
import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';
import { requireService } from 'src/packages/di';

interface AlertsManagerWidgetOptions {
  type: string;
  stateParams: Partial<AlertsManagerWidgetStateParams>;
  wellId: number | null;
  isEditMode: boolean;
}

export type TAlertsManagerSerializedInternalState = {
  stateParams: Partial<AlertsManagerWidgetStateParams>;
  wellId: number | null;
  isEditMode: boolean;
};

export class AlertsManagerWidgetEntity extends BaseWidgetEntityWithWellId {
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: {
      w: 300,
      h: 200,
    },
    maxInPixels: null,
    min: null,
    max: {
      w: 256,
      h: 100,
    },
  };

  @observable state: AlertsManagerWidgetState | null = null;
  @observable type: string;
  @observable isEditMode: boolean;
  @observable wellId: number | null;
  @observable.ref stateParams: Partial<AlertsManagerWidgetStateParams>;
  @observable inActiveTab: boolean = false;
  @observable wellboreName: string | null = null;

  constructor(
    { type, stateParams, wellId, isEditMode }: AlertsManagerWidgetOptions,
    widgetOptions: TWidgetOptions,
    private readonly i18 = requireService('i18'),
  ) {
    super(widgetOptions);

    this.type = type;
    this.isEditMode = isEditMode;
    this.wellId = wellId;
    this.stateParams = stateParams;

    makeObservable(this);
  }

  getNameT(): string {
    return 'widgets:AlertsManager.title';
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setWellboreName(name: string): void {
    this.wellboreName = name;
  }

  @action.bound
  setStateParams(stateParams: Partial<AlertsManagerWidgetStateParams>): void {
    this.stateParams = stateParams;
  }

  @action.bound
  setFullscreen(isFullscreen: boolean): void {
    this.isFullscreen = isFullscreen;
    this.state?.setAppParams?.({ active: this.inActiveTab });
  }

  get isControlWidget(): boolean {
    return false;
  }

  get title(): string {
    return this.i18.t(this.getNameT());
  }

  @action.bound
  setState(state: AlertsManagerWidgetState | null): void {
    this.state = state;
  }

  @action.bound
  setEditMode(value: boolean): void {
    this.isEditMode = value;

    if (value && !this.isFullscreen) {
      this.setFullscreen(true);
    }
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
    this.state?.setAppParams({
      active: this.inActiveTab,
    });
  }

  override destroy() {
    this.state?.destroy();
  }
}
